import { Box, Stack } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { v4 as uuidv4 } from 'uuid';

import TableWidget from '@/components/TableWidget';
import { REPORTS_COLORS } from '@/constants/chartcolors.constants';
import ReportParagraph from '@/features/ReportsTemplateParagraph/ReportsParagraph';
import ReportsTemplateSectionTitle from '@/features/ReportsTemplateSectionTitle/ReportsTemplateSectionTitle';

import type { CaseTableType } from '../ReportsPage.types';
import { FIRST_PAGE_ROWS, ROWS_PER_PAGE } from './CasesPage.constants';
import NetworkCasesGraph from './NetworkCasesGraph';
import TicketedCasesGraph from './TicketedCasesGraph';

export const casesPageTableColumns: GridColDef[] = [
  {
    field: 'value',
    headerName: 'Title',
    flex: 1,
    renderCell: (params) => (
      <div key={uuidv4()} style={{ fontSize: 10, textAlign: 'center' }}>
        {params.row.value}
      </div>
    ),
    renderHeader: () => (
      <div
        style={{
          fontWeight: 'bold',
          fontSize: '10px',
          textTransform: 'uppercase'
        }}
      >
        Title
      </div>
    )
  },
  {
    field: 'count',
    headerName: 'count',
    minWidth: 50,
    headerAlign: 'center',
    align: 'center',
    renderHeader: () => (
      <div
        style={{
          fontWeight: 'bold',
          fontSize: '10px',
          textTransform: 'uppercase',
          color: REPORTS_COLORS.CASES
        }}
      >
        Count
      </div>
    ),
    renderCell: (params) => (
      <div
        key={uuidv4()}
        style={{
          fontSize: 10,
          display: 'flex'
        }}
      >
        {params.row.count}
      </div>
    )
  },
  {
    field: 'ticketedCount',
    headerName: 'Ticketed Count',
    minWidth: 130,
    headerAlign: 'center',
    align: 'center',
    renderHeader: () => (
      <div
        style={{
          fontWeight: 'bold',
          fontSize: '10px',
          color: REPORTS_COLORS.TICKETED_CASES,
          textTransform: 'uppercase'
        }}
      >
        Ticketed Count
      </div>
    ),
    renderCell: (params) => (
      <div
        key={uuidv4()}
        style={{
          fontSize: 10,
          display: 'flex'
        }}
      >
        {params.row.ticketedCount}
      </div>
    )
  }
];

export const ticketedCasesPageTableColumns: GridColDef[] = [
  {
    field: 'value',
    headerName: 'Title',
    flex: 1,
    renderCell: (params) => (
      <div style={{ fontSize: 10, textAlign: 'center' }}>
        {params.row.value}
      </div>
    ),
    renderHeader: () => (
      <div
        style={{
          fontWeight: 'bold',
          fontSize: '10px',
          textTransform: 'uppercase'
        }}
      >
        Title
      </div>
    )
  },
  {
    field: 'count',
    headerName: 'Ticketed Count',
    minWidth: 130,
    headerAlign: 'center',
    align: 'center',
    renderHeader: () => (
      <div
        style={{
          fontWeight: 'bold',
          fontSize: '10px',
          color: REPORTS_COLORS.TICKETED_CASES,
          textTransform: 'uppercase'
        }}
      >
        Ticketed Count
      </div>
    ),
    renderCell: (params) => (
      <div
        key={uuidv4()}
        style={{
          fontSize: 10,
          display: 'flex'
        }}
      >
        {params.row.count}
      </div>
    )
  }
];

export const mappedTablePages = (
  tableData: CaseTableType[],
  type: 'network' | 'log' | 'ticketed network' | 'ticketed log'
) => {
  if (tableData.length === 0) {
    return [];
  }

  const pages = [];
  const columns = type.includes('ticketed')
    ? ticketedCasesPageTableColumns
    : casesPageTableColumns;

  const getGraphText = (): string => {
    switch (type) {
      case 'network':
        return 'This chart shows the most frequently triggered network cases by sensor.';
      case 'ticketed network':
        return 'This chart shows the number of network cases escalated to tickets each day.';
      case 'ticketed log':
        return 'This chart shows the number of log cases escalated to tickets each day.';
      case 'log':
        return 'This chart shows the most frequently triggered log cases by signature.';
      default:
        return '';
    }
  };

  const getSectionTitle = (): string => {
    switch (type) {
      case 'network':
        return 'Network Cases';
      case 'log':
        return 'Log Cases';
      default:
        return '';
    }
  };

  const isNetworkType = type === 'network';
  const isTicketedType = type.includes('ticketed');

  // Calculate total pages based on type
  const totalPages = isNetworkType
    ? Math.ceil(
        Math.max(0, tableData.length - FIRST_PAGE_ROWS) / ROWS_PER_PAGE
      ) + 1
    : Math.ceil(tableData.length / ROWS_PER_PAGE);

  for (let pageIndex = 0; pageIndex < totalPages; pageIndex += 1) {
    const isFirstPage = pageIndex === 0;
    const isLastPage = pageIndex === totalPages - 1;

    let startIndex: number;
    let endIndex: number;
    let pageRows: CaseTableType[];

    if (isFirstPage && isNetworkType) {
      // First page for network type: show graph + limited table rows
      startIndex = 0;
      endIndex = FIRST_PAGE_ROWS;
      pageRows = tableData.slice(startIndex, endIndex);

      pages.push(
        <>
          <ReportsTemplateSectionTitle>
            {getSectionTitle()}
          </ReportsTemplateSectionTitle>
          <Stack spacing={2} direction="row" sx={{ width: '100%' }}>
            <Box
              style={{
                width: '100%',
                height: '300px',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box
                data-testid="network-cases-graph"
                sx={{ height: '100%', width: '100%' }}
              >
                <NetworkCasesGraph />
              </Box>
              <ReportParagraph fontSize="12px">
                {getGraphText()}
              </ReportParagraph>
            </Box>
          </Stack>
          <TableWidget
            key={uuidv4()}
            title={`${type} Cases by Signature`}
            columns={columns}
            rows={pageRows}
            header
            loading={false}
            cellAlignment="center"
            variant="reports"
            rowHeight={25}
            customHeaderHeight={30}
          />
        </>
      );
    } else {
      // Calculate rows for subsequent pages
      if (isNetworkType) {
        startIndex = FIRST_PAGE_ROWS + (pageIndex - 1) * ROWS_PER_PAGE;
        endIndex = startIndex + ROWS_PER_PAGE;
      } else {
        startIndex = pageIndex * ROWS_PER_PAGE;
        endIndex = startIndex + ROWS_PER_PAGE;
      }

      pageRows = tableData.slice(startIndex, endIndex);

      // Handle ticketed types with graphs on last page
      if (isTicketedType && isLastPage) {
        const shouldSplitLastPage = pageRows.length > FIRST_PAGE_ROWS;

        if (shouldSplitLastPage) {
          // Split into table page and graph page
          pages.push(
            <TableWidget
              key={uuidv4()}
              title={`${type} Cases by Signature`}
              columns={columns}
              rows={pageRows}
              header
              loading={false}
              cellAlignment="center"
              variant="reports"
              rowHeight={25}
              customHeaderHeight={30}
            />
          );

          pages.push(
            <>
              <TicketedCasesGraph
                type={type.includes('network') ? 'network' : 'log'}
              />
              <ReportParagraph fontSize="12px">
                {getGraphText()}
              </ReportParagraph>
            </>
          );
        } else {
          // Combine table and graph on same page
          pages.push(
            <>
              <TableWidget
                key={uuidv4()}
                title={`${type} Cases by Signature`}
                columns={columns}
                rows={pageRows}
                header
                loading={false}
                cellAlignment="center"
                variant="reports"
                rowHeight={25}
                customHeaderHeight={30}
              />
              <TicketedCasesGraph
                type={type.includes('network') ? 'network' : 'log'}
              />
              <ReportParagraph fontSize="12px">
                {getGraphText()}
              </ReportParagraph>
            </>
          );
        }
      } else {
        // Regular table page
        const shouldAddSectionTitle =
          isFirstPage && !isNetworkType && !isTicketedType;

        pages.push(
          <>
            {shouldAddSectionTitle && (
              <ReportsTemplateSectionTitle>
                {getSectionTitle()}
              </ReportsTemplateSectionTitle>
            )}
            <TableWidget
              key={uuidv4()}
              title={`${type} Cases by Signature`}
              columns={columns}
              rows={pageRows}
              header
              loading={false}
              cellAlignment="center"
              variant="reports"
              rowHeight={25}
              customHeaderHeight={30}
            />
          </>
        );
      }
    }
  }

  return pages;
};

export default casesPageTableColumns;
